import React from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "../../../lib/utils";
import "../AgenticGateway.css";

function principalLabel(pr: any): string {
  if (!pr) return "?";
  if (typeof pr === "string") return pr;
  return pr.username || pr.clientId || (pr.id ? `${String(pr.id).slice(0, 8)}…` : pr.type || "?");
}

interface Hop {
  aud: string | null;
  actor: string | null;
  fromFallback: string | null;
  toFallback: string | null;
  scope: string | null;
  jti: string | null;
  ttlSeconds: number | null;
  decision: string | null;
  hasMint: boolean;
  noMintReason: string | null;
  chain: any[];
}

// Why a hop has no minted token: prefer the enriched PDP row (real policy id + reason), then the connectivity
// error, then a sensible default — so an empty hop box explains itself instead of showing blanks.
function noMintReason(evs: any[], decision: string | null): string {
  const deny =
    evs.find((e) => e.pdpDecision === "DENY" && (e.pdpReason || e.pdpPolicyId)) ||
    evs.find((e) => e.pdpDecision === "DENY" || e.status === "DENIED");
  if (deny) {
    if (deny.pdpReason) return deny.pdpReason;
    if (deny.pdpPolicyId) return `Denied by policy [${deny.pdpPolicyId}]`;
    return "Denied by policy.";
  }
  const err = evs.find((e) => e.errorMessage);
  if (err && err.errorMessage) return err.errorMessage;
  if (decision === "ALLOW")
    return "Authorized, but the target was not reachable — rejected at the connectivity gate before the mint step.";
  return "Rejected before a delegation token was minted.";
}

function buildHops(events: any[]): Hop[] {
  const order: string[] = [];
  const byHop: Record<string, any[]> = {};
  events.forEach((e) => {
    const cid = e.correlationId || "__nohop__";
    if (!byHop[cid]) {
      byHop[cid] = [];
      order.push(cid);
    }
    byHop[cid].push(e);
  });

  const hops: Hop[] = [];
  order.forEach((cid) => {
    const evs = byHop[cid];
    const mint = evs.find((e) => e.eventType === "STS_TOKEN_MINTED");
    const p = (mint && (mint.responsePayload || mint.response_payload)) || {};
    let decision: string | null = null;
    for (const e of evs) {
      if (e.pdpDecision) {
        decision = e.pdpDecision;
        break;
      }
      if (e.status === "DENIED" || (e.eventType || "").includes("DENIED") || (e.eventType || "").includes("REJECTED")) {
        decision = "DENY";
        break;
      }
      if (e.eventType === "CAPABILITY_ACCESS_GRANTED" || e.eventType === "PDP_DECISION_RENDERED") decision = "ALLOW";
    }
    const chain = Array.isArray(p.act_chain) ? p.act_chain : Array.isArray(p.actChain) ? p.actChain : [];
    if (!mint && !decision) return;
    // Fallback route for hops with no minted token (DENY, or ALLOW-but-unreachable): the gateway still knows
    // the intended caller→target from the PDP/orchestration events (agentName → serverName).
    let evFrom: string | null = null, evTo: string | null = null;
    for (const e of evs) {
      if (!evFrom && e.agentName) evFrom = String(e.agentName).replace(/ v\d.*$/, "");
      if (!evTo && e.serverName) evTo = e.serverName;
    }
    hops.push({
      aud: p.aud || null,
      actor: p.act ? principalLabel(p.act) : chain.length ? principalLabel(chain[chain.length - 1]) : null,
      fromFallback: evFrom,
      toFallback: evTo,
      scope: p.scope || null,
      jti: p.jti || null,
      ttlSeconds: p.ttlSeconds != null ? p.ttlSeconds : null,
      decision,
      hasMint: !!mint,
      noMintReason: mint ? null : noMintReason(evs, decision),
      chain,
    });
  });
  return hops;
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between gap-2.5 text-[11px] my-0.5">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={cn("text-right break-all text-foreground/90", mono && "font-mono")}>{value}</span>
    </div>
  );
}

export default function GatewayOboChain({ events }: { events: any[] }) {
  const hops = buildHops(events || []);
  if (!hops.length) return null;
  return (
    <div className="min-w-0">
      {/* Dark, always-on scrollbar so the slider doesn't flash white when the pointer leaves the row. */}
      <style>{`
        .gw-obo-scroll{scrollbar-width:thin;scrollbar-color:hsl(var(--muted-foreground)/0.4) transparent;}
        .gw-obo-scroll::-webkit-scrollbar{height:8px;}
        .gw-obo-scroll::-webkit-scrollbar-track{background:transparent;}
        .gw-obo-scroll::-webkit-scrollbar-thumb{background:hsl(var(--muted-foreground)/0.35);border-radius:9999px;}
        .gw-obo-scroll::-webkit-scrollbar-thumb:hover{background:hsl(var(--muted-foreground)/0.55);}
      `}</style>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">OBO delegation chain — per hop</div>
      {/* min-w-0 + overflow-x-auto: this row is the ONLY horizontal scroller — the window stays fixed-width */}
      <div className="flex items-stretch gap-2 overflow-x-auto min-w-0 pb-1.5 gw-obo-scroll">
        {hops.map((h, i) => {
          const denied = h.decision === "DENY";
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div className="flex items-center text-muted-foreground shrink-0">
                  <ArrowRight className="w-4 h-4" />
                </div>
              )}
              <div className="min-w-[210px] flex-1 shrink-0 rounded-lg border border-border ag-surface px-3.5 py-3">
                <div className="flex items-center justify-between gap-2 mb-2.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Hop {i + 1}</span>
                  <span
                    className={cn(
                      "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border",
                      denied ? "text-red-600 dark:text-red-400 border-red-500/40 bg-red-500/10" : "text-emerald-600 dark:text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                    )}
                  >
                    {h.decision || (h.hasMint ? "ALLOW" : "—")}
                  </span>
                </div>
                <KV label="Actor" value={h.actor || h.fromFallback} />
                <KV label="Target" value={h.aud || h.toFallback} mono />
                <KV label="Scope" value={h.scope} mono />
                <KV label="TTL" value={h.ttlSeconds != null ? `${h.ttlSeconds}s` : null} />
                <KV label="jti" value={h.jti ? `${String(h.jti).slice(0, 14)}…` : null} mono />
                {h.chain && h.chain.length > 0 && (
                  <div className="mt-2.5 pt-2 border-t border-dashed border-border">
                    <div className="text-[10px] text-muted-foreground">act_chain</div>
                    <div className="text-[11px] text-foreground/90 break-words mt-0.5">{h.chain.map(principalLabel).join(" → ")}</div>
                  </div>
                )}
                {!h.hasMint && h.noMintReason && (
                  <div className="mt-2.5 rounded-md border border-amber-500/35 bg-amber-500/10 px-2.5 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">No token minted</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                      <span className="text-muted-foreground/70">Reason:</span> {h.noMintReason}
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
